<template>
    <div class="vehicle-report">
        <b-field class="datetime-field" :label="$t('dt_picker.date_from')">
            <b-datetimepicker
                :max-datetime="maxDate"
                :datetime-formatter="datetimeFormatter"
                locale="en-GB"
                v-model="dateStart"
                :mobile-native="false"
                ref="dtFrom"
                editable>
                <template slot="left">
                    <button class="button" @click="$refs.dtFrom.toggle()">
                        <span>{{ $t('dt_picker.cancel') }}</span>
                    </button>
                </template>
                <template slot="right">
                    <button class="button" @click="$refs.dtFrom.toggle()">
                        <span>{{ $t('dt_picker.confirm') }}</span>
                    </button>
                </template>
            </b-datetimepicker>
        </b-field>
        <b-field class="datetime-field" :label="$t('dt_picker.date_to')">
            <b-datetimepicker
                :max-datetime="maxDate"
                :datetime-formatter="datetimeFormatter"
                locale="en-GB"
                v-model="dateEnd"
                :mobile-native="false"
                ref="dtTo"
                editable>
                <template slot="left">
                    <button class="button" @click="$refs.dtTo.toggle()">
                        <span>{{ $t('dt_picker.cancel') }}</span>
                    </button>
                </template>
                <template slot="right">
                    <button class="button" @click="$refs.dtTo.toggle()">
                        <span>{{ $t('dt_picker.confirm') }}</span>
                    </button>
                </template>
            </b-datetimepicker>
        </b-field>
        <b-message type="is-danger" v-if="errorMessage">
            {{ errorMessage }}
        </b-message>
        <div>
            <div class="dt-control-row">
                <div>
                    <a role="button" class="pagination-previous" @click="datetimeAddDays(-1)">
                    <span class="icon has-text-primary is-clickable">
                        <i class="fas fa-angle-left"></i>
                    </span>
                    </a>
                </div>
                <b-button :loading="isLoading" class="is-right" @click="getVehicleReport">
                    {{ $t('vehicle_report.get_report') }}
                </b-button>
                <div>
                    <a role="button" class="pagination-next" @click="datetimeAddDays(1)" :disabled="dateNextDisabled">
                    <span class="icon has-text-primary is-clickable">
                    <i class="fas fa-angle-right"></i>
                    </span>
                    </a>
                </div>
            </div>
            <div class="control" style="margin-top: 20px; display: inline-block;">
                <b-switch v-model="mobileCards">
                    {{ $t('vehicle_report.collapse_rows') }}
                </b-switch>
            </div>
        </div>

        <div class="vehicle-report-table" id="vehicle-report-table">
            <b-table
                :data="isLoading ? [] : dailySummaries"
                :narrowed="true"
                :striped="true"
                :loading="isLoading"
                :mobile-cards="mobileCards">
                <b-table-column field="date" :label="$t('vehicle_report.date')" width="40" v-slot="props">
                    {{ new Date(props.row.date).toLocaleDateString('en-GB') }}
                </b-table-column>

                <b-table-column field="departureAddress"
                                :label="$t('vehicle_report.dep_address')"
                                width="250"
                                v-slot="props">
                    {{ props.row.departureAddress }}
                </b-table-column>

                <b-table-column field="arrivalAddress"
                                :label="$t('vehicle_report.arr_address')"
                                v-slot="props">
                    {{ props.row.arrivalAddress }}
                </b-table-column>

                <b-table-column field="departureTime"
                                :label="$t('vehicle_report.dep_time')"
                                width="90"
                                centered
                                v-slot="props">
                    {{ props.row.departureTime }}
                </b-table-column>

                <b-table-column field="arrivalTime"
                                :label="$t('vehicle_report.arr_time')"
                                width="90"
                                centered
                                v-slot="props">
                    {{ props.row.arrivalTime }}
                </b-table-column>

                <b-table-column field="distance"
                                :label="$t('vehicle_report.distance')"
                                centered
                                numeric
                                v-slot="props">
                    {{ props.row.distance }}
                </b-table-column>

                <b-table-column field="departureOdometer"
                                :label="$t('vehicle_report.dep_odometer')"
                                centered
                                numeric
                                v-slot="props">
                    {{ props.row.departureOdometer }}
                </b-table-column>

                <b-table-column field="arrivalOdometer"
                                :label="$t('vehicle_report.arr_odometer')"
                                centered
                                numeric
                                v-slot="props">
                    {{ props.row.arrivalOdometer }}
                </b-table-column>

                <b-table-column field="cumulativeFuelUsed"
                                :label="$t('vehicle_report.cum_fuel_used')"
                                centered
                                numeric
                                v-slot="props">
                    {{ props.row.cumulativeFuelUsed }}
                </b-table-column>

                <b-table-column field="driver"
                                :label="$t('vehicle_report.driver')"
                                v-slot="props">
                    {{ props.row.driver }}
                </b-table-column>
            </b-table>
        </div>
    </div>
</template>

<script>

export default {
    name: 'VehicleReport',
    data() {
        return {
            isLoading: true,
            mobileCards: false,
            errorMessage: null,
            maxDate: new Date(),
            dateStart: null,
            dateEnd: null,
            dailySummaries: []
        };
    },
    methods: {
        clearMessages() {
            this.errorMessage = null;
        },
        clearState() {
            this.isLoading = false;
            let now = this.$moment();
            this.dateStart = now.startOf('day').toDate();
            this.dateEnd = now.endOf('day').toDate();
            this.dailySummaries = [];
        },
        datetimeAddDays(days) {
            if (days) {
                let newEnd = this.$moment(this.dateEnd).add(days, 'days');

                if (newEnd.isSameOrBefore(this.$moment(), 'days')) {
                    this.dateStart = this.$moment(this.dateStart).add(days, 'days').toDate();
                    this.dateEnd = newEnd.toDate();
                    this.getVehicleReport();
                }
            }
        },
        getVehicleReport() {
            this.clearMessages();
            this.dailySummaries = [];
            this.isLoading = true;

            let start = this.$moment(this.dateStart);
            let end = this.$moment(this.dateEnd);

            let params = {
                from: start.format(),
                to: end.format()
            };

            this.$store.state.api.fetchVehicleReport(this.$store.getters.selectedDevice.id, params)
                .then(data => {
                    if (data && data.length) {
                        this.dailySummaries = data;
                    } else {
                        this.$toastSuccess(this.$t('vehicle_report.no_data_for_period'));
                        this.dailySummaries = [];
                    }
                })
                .catch(() => {
                    this.$toastGenericError();
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        datetimeFormatter(dt) {
            return this.$moment(dt).format('DD/MM/YYYY HH:mm');
        }
    },
    computed: {
        dateNextDisabled() {
            return !this.$moment(this.dateEnd).add(1, 'days').isSameOrBefore(this.$moment(), 'days');
        },
        device: function() {
            return this.$store.getters.selectedDevice;
        }
    },
    mounted() {
        this.clearState();
        this.$root.$on('getVehicleReport', () => {
            this.getVehicleReport();
        });
    },
    beforeDestroy() {
        this.$store.commit('devicePanelFullScreen', false);
        this.clearState();
    }
};
</script>

<style lang="scss" scoped>
.vehicle-report-table {
    margin-top: 20px;
    font-size: 0.8rem;
}

.vehicle-report .field.datetime-field {
    width: 50%;
    display: inline-block;
}

.vehicle-report {
    .dt-control-row {
        margin-top: 20px;
        display: flex;
        justify-content: space-between;
    }

    .field.datetime-field:first-child {
        padding-right: 10px;
    }
}
</style>

<style lang="scss">
.vehicle-report-table .table th {
    vertical-align: middle;
}

.table-wrapper {
    max-height: 100%;

    .table {
        flex: 0 0 auto;
    }
}
</style>
